// Gameplay constants for Farm ATC.
//
// Positions, distances and speeds are fractions of the scope (0..1 on each
// axis); times are seconds of game clock.

export const DAY_LENGTH = 60;            // one shift-day: four time-of-day slots
export const MAX_INCIDENTS = 3;          // separation incidents that end a shift

export const START_SPAWN_INTERVAL = 4.2; // seconds between filed plans on day 1
export const MIN_SPAWN_INTERVAL = 1.8;
export const SPAWN_INTERVAL_STEP = 0.3;  // faster filing each new day
export const START_MAX_TRAFFIC = 8;      // filed + airborne cap on day 1
export const MAX_TRAFFIC_CAP = 13;

export const CONFLICT_DIST = 0.055;      // real-time separation ring (airborne)
export const COLLISION_DIST = 0.026;     // real-time collision -> incident
export const PREDICT_DIST = 0.062;       // predicted-conflict threshold (pre-flight)
export const ARRIVE_DIST = 0.014;
export const PREDICT_SAMPLES = 10;       // samples along an overlap window

export const SPEED = 0.05;               // still-air ground speed
export const LEAD_MIN = 6;               // filed lead time before departure
export const LEAD_MAX = 9;
export const OPENING_PLANS = 3;          // plans already filed when a shift starts
export const OPENING_LEAD = 8;           // first opening plan's lead time...
export const OPENING_STAGGER = 4;        // ...and the gap between the opening plans
export const DELAY_STEP = 4;             // seconds added per Delay request
export const WIND_RELEASE_LEAD = 3;      // countdown a wind-held plan gets once its hold lifts
export const PREDICT_URGENT = 3;         // seconds before launch a predicted conflict turns urgent

export const WIND_SCALE = SPEED / 25;    // 25mph fully along a leg = +-SPEED
export const MIN_SPEED_FACTOR = 0.35;    // headwind floor (never stall)
export const MAX_SPEED_FACTOR = 1.9;     // tailwind ceiling
export const MAX_WIND_INPUT = 40;        // mph, Wind Briefing input range

// Scoring, in one table: points per event, applied by scoreBreakdown() in
// src/core/score.js. Landings earn, preventing a near miss earns more, being
// careful is free (a delay's cost is slower traffic), and an incident costs a lot.
export const SCORING = {
  landing: 10,      // per drone landed at its destination
  nearMiss: 15,     // per conflict a Delay, Reroute or Hold removed (once per pair of flights)
  adjustment: 0,    // per Delay, Reroute or Hold
  fieldScouted: 5,  // per work-order field a scout swept clear (#13)
  fieldSprayed: 25, // per work-order field sprayed end to end (#13)
  separationLoss: -10, // per pair of aircraft that came inside the separation ring (once per pair)
  hazardHandled: 5, // per field or hub alert your ground crew cleared, or power line a scout found (#31)
  birdStrike: -25,  // per aircraft that flew into a bird flock (#31)
  incident: -50,    // per separation incident
};

// ---- Skill levels, season and speed of play (#13) ---------------------------
// A Standard shift's hub traffic depends on your level: how often plans are
// filed, how much traffic is up at once, and how much warning a plan gives
// before it launches. The debrief promotes you (work order done, no
// incidents, at least `promoteScore` points), demotes you (the shift ended on
// incidents, or scored below zero), or keeps you where you are. Senior
// operator is 0.0.3's pace, which proved expert-level for new players.
export const LEVELS = [
  {
    id: 'trainee', label: 'Trainee',
    spawnInterval: 7.5, minSpawnInterval: 4.5, maxTraffic: 5, trafficCap: 7, leadMin: 10, leadMax: 14, promoteScore: 120,
    alertInterval: 70, maxAlerts: 1,
  },
  {
    id: 'operator', label: 'Operator',
    spawnInterval: 5.8, minSpawnInterval: 3.0, maxTraffic: 6, trafficCap: 9, leadMin: 8, leadMax: 12, promoteScore: 200,
    alertInterval: 55, maxAlerts: 2,
  },
  {
    id: 'senior', label: 'Senior operator',
    spawnInterval: START_SPAWN_INTERVAL, minSpawnInterval: MIN_SPAWN_INTERVAL, maxTraffic: START_MAX_TRAFFIC,
    trafficCap: MAX_TRAFFIC_CAP, leadMin: LEAD_MIN, leadMax: LEAD_MAX, promoteScore: 300,
    alertInterval: 45, maxAlerts: 2,
  },
  {
    id: 'chief', label: 'Chief controller',
    spawnInterval: 3.4, minSpawnInterval: 1.5, maxTraffic: 9, trafficCap: 15, leadMin: 5, leadMax: 8, promoteScore: null,
    alertInterval: 35, maxAlerts: 3,
  },
];
export const DEFAULT_LEVEL = 'senior'; // a sim created without a level (tests, the balance checks)

// A season is the whole game: up to SEASON_DAYS_MAX shift-days (a day is
// DAY_LENGTH seconds of game clock). Lesson 1 is training and doesn't use any.
export const SEASON_DAYS_MAX = 100;

// Speed of play: a multiplier on the game clock. Fast-forward runs a season
// to its end quickly during development. Faster play earns more: `bonus`
// multiplies the points a shift earns (not its penalties). The bonus rises
// gently and levels off, so it is not proportional to the speed (#13).
export const PACES = [
  { value: 0.5, label: 'Slow', bonus: 0.9 },
  { value: 0.75, label: 'Relaxed', bonus: 1 },
  { value: 1, label: 'Normal', bonus: 1.1 },
  { value: 1.5, label: 'Fast', bonus: 1.2 },
  { value: 2, label: 'Fast-forward', bonus: 1.25 },
  { value: 4, label: 'Fast-forward', bonus: 1.3 },
  { value: 8, label: 'Fast-forward', bonus: 1.3 },
];
export const DEFAULT_PACE = 0.75;

// ---- Crop-dusting operations (#11, #13) -------------------------------------
// The farmer's own aircraft. Speeds are scope fractions per second like SPEED;
// `workSpeed` is the speed flying its pattern over a field; `maxWind` (mph) is
// the most it will fly in. A duster only sprays inside the spray window.
export const SPRAY_WIND_MIN = 3;   // mph; calmer than this risks an inversion (spray hangs and drifts)
export const SPRAY_WIND_MAX = 10;  // mph; windier than this drifts off the field

export const OPS_TYPES = [
  {
    id: 'scout', label: 'Scout drone', prefix: 'SCOUT', color: '#8ac1e0',
    task: 'scout', speed: SPEED * 1.35, workSpeed: SPEED * 1.1, maxWind: 25, pad: 1, lanes: 3,
    power: 'ev', endurance: 80, refill: 24, // seconds of flight on a full battery; seconds to charge from empty
  },
  {
    id: 'duster', label: 'Crop-dusting helicopter', prefix: 'DUSTER', color: '#d8c35b',
    task: 'spray', speed: SPEED * 1.1, workSpeed: SPEED * 0.8, maxWind: 30, pad: 0, lanes: 7,
    power: 'avgas', endurance: 300, refill: 8, // seconds of flight on full tanks; seconds to refuel from empty
  },
];

// Aircraft slots in the hangar. The first scout and duster are always
// available; the rest unlock with career points. `later` ones are listed but
// not flyable yet (#12).
export const HANGAR = [
  { id: 'scout1', type: 'scout', points: 0 },
  { id: 'duster1', type: 'duster', points: 0 },
  { id: 'duster2', type: 'duster', points: 150 },
  { id: 'scout2', type: 'scout', points: 300 },
  { id: 'spraydrone', label: 'Spray drone', later: true },
  { id: 'fixedwing', label: 'Fixed-wing ag plane', later: true },
];

// Lesson 1 (orientation): two fields, calm steady wind, no other traffic.
export const ORIENTATION_FIELDS = [0, 1];
export const ORIENTATION_WIND_SPEED = 6;
export const ORIENTATION_WIND_DIR = 315;

export const FIELDS = [
  { name: 'North Forty',  x: 0.10, y: 0.08, w: 0.32, h: 0.30 },
  { name: 'Home Quarter', x: 0.58, y: 0.08, w: 0.32, h: 0.30 },
  { name: 'Back Forty',   x: 0.10, y: 0.62, w: 0.32, h: 0.30 },
  { name: 'Creek Bottom', x: 0.58, y: 0.62, w: 0.32, h: 0.30 },
];

// `supplies`: what a hub can refill (#32). The barn has the fuel tank; the
// shed has the solar array and chargers.
export const HUBS = [
  { name: 'Barn Hub', x: 0.34, y: 0.47, supplies: ['avgas'] },
  { name: 'Shed Hub', x: 0.66, y: 0.47, supplies: ['ev'] },
];

// maxWind (mph): forecast speed above which a type can't be dispatched.
// Light scouting airframes tolerate the least, heavy ag equipment the most.
// Placeholder values, not tuned game design.
// `power` (#32): electric (EV) or aviation gas (AVGAS).
export const TYPES = [
  { prefix: 'SCOUT',   color: '#8ac1e0', maxWind: 15, power: 'ev' },
  { prefix: 'ROVER',   color: '#e2ab48', maxWind: 24, power: 'ev' },
  { prefix: 'TRACTOR', color: '#c98a5e', maxWind: 28, power: 'avgas' },
  { prefix: 'PICKER',  color: '#8fbf78', maxWind: 20, power: 'ev' },
];

// Wind direction is the bearing it blows FROM (aviation convention:
// "wind from the NW at 12mph").
export const COMPASS = [
  { deg: 0, label: 'N' },   { deg: 45, label: 'NE' },  { deg: 90, label: 'E' },  { deg: 135, label: 'SE' },
  { deg: 180, label: 'S' }, { deg: 225, label: 'SW' }, { deg: 270, label: 'W' }, { deg: 315, label: 'NW' },
];

export const TIME_SLOTS = ['morning', 'afternoon', 'evening', 'night'];
export const TIME_SLOT_LABELS = { morning: 'Morning', afternoon: 'Afternoon', evening: 'Evening', night: 'Night' };

export const DEFAULT_WIND = {
  morning:   { speed: 6,  dir: 315 }, // light out of the NW
  afternoon: { speed: 16, dir: 225 }, // building SW crosswind, grounds SCOUTs
  evening:   { speed: 9,  dir: 270 }, // easing westerly
  night:     { speed: 3,  dir: 0 },   // near calm, out of the N
};

// ---- Field and hub alerts (#31) ----------------------------------------------
// Things that put real farm flying on hold, kept light: the point is a fun
// interruption plus a line of real-world awareness (`fact`), not a simulation.
// `seconds` is how long it lasts by itself; `crew` is how long the ground crew
// takes to clear it when sent (null: the crew can't help). Each shows up only
// in Standard shifts; your level sets how often (LEVELS[].alertInterval) and
// how many at once (LEVELS[].maxAlerts).
export const ALERT_FIRST_AFTER = 30; // game seconds into a shift before the first alert
export const ALERT_KINDS = {
  person: {
    title: 'Person in the field', weight: 3, seconds: 22, crew: 5, action: 'Send ground crew',
    detail: 'Spraying in {field} pauses until they are clear',
    fact: 'Real rule: aerial spraying must stop while anyone is within 100 ft of the aircraft (EPA application exclusion zone).',
  },
  birds: {
    title: 'Bird flock', weight: 3, seconds: null, crew: null, action: null,
    detail: 'Drifting across the farm: hold or route around it',
    fact: 'Bird strikes damage aircraft, and eagles have torn drones out of the sky: give flocks room.',
  },
  tower: {
    title: 'New tower', weight: 2, seconds: null, crew: null, action: null,
    detail: 'Scout {field} again before spraying it',
    fact: 'Wire and tower strikes cause about a quarter of fatal ag-aviation accidents. Towers can go up in hours; scouting finds them.',
  },
  padBlocked: {
    title: 'Pad blocked', weight: 2, seconds: 30, crew: 7, action: 'Send tow crew',
    detail: 'A broken craft is on {hub}: nothing can land there',
    fact: 'One broken-down craft on a pad stops every landing there until it is towed clear.',
  },
  lowPower: {
    title: 'Low power', weight: 2, seconds: 40, crew: 6, action: 'Start backup generator',
    detail: 'Aircraft landing at {hub} recharge slowly',
    fact: 'Spray-drone crews pause every hour or two while batteries catch up: charging is limited by generator power.',
  },
  // raised by energy.js when one of yours lands where it can't refill (#32), never at random
  stranded: {
    title: 'Stranded', weight: 0, seconds: null, crew: 20, action: 'Send a supply truck',
    detail: '{call} is low and {base} has no {power}',
    fact: 'Electric craft need a charger and gas craft need avgas: land where there is neither and nothing refills them until a truck drives out.',
  },
  // raised by zones.js when a landing pattern is full (#33), never at random
  emergency: {
    title: 'Emergency landing', weight: 0, seconds: null, crew: null, action: 'Land at nearest free pad',
    detail: '{call} must land now: {reason}',
    fact: 'Every flight plan names an alternate: when the landing pattern is full or the battery runs low, an aircraft lands at the nearest safe place.',
  },
};
// A power line a scout finds while sweeping (not an alert: a find).
export const WIRE_FIND_CHANCE = 0.35;
export const WIRE_FACT = 'Most wire strikes happen to power lines inside the field being treated: a scout pass marks them first.';
export const FLOCK_RADIUS = 0.06;
export const FLOCK_SPEED = 0.028;   // scope fractions per second
export const LOW_POWER_CHARGE = 0.25; // EV charging speed at a hub on low power (#31, #32)

// ---- Landing zones and capacity (#33) ----------------------------------------
// Standard shifts only. Each hub has HUB_PADS pads; your own aircraft keep a
// pad reserved at their base, and a hub drone that lands sits on a pad for
// TURNAROUND seconds. Drones that find their hub full circle in its landing
// pattern (up to PATTERN_CAP); one more, or one that circles too long, must
// make an emergency landing at a free pad within EMERGENCY_SECONDS, or it
// comes down in a field (an incident). Each field has a one-aircraft pad at
// its inner corner, just outside it in the lane between fields: an
// alternate landing zone.
export const HUB_PADS = 3;
export const PATTERN_CAP = 2;
export const TURNAROUND = 6;          // seconds a landed hub drone occupies its pad
export const EMERGENCY_SECONDS = 15;
export const PATTERN_PATIENCE = 25;   // seconds circling before the battery runs low
export const HOVER_PATIENCE = 20;     // seconds one of yours can hover over a blocked pad
export const FIELD_PADS = [
  { fieldIdx: 0, x: 0.455, y: 0.33 },
  { fieldIdx: 1, x: 0.545, y: 0.33 },
  { fieldIdx: 2, x: 0.455, y: 0.67 },
  { fieldIdx: 3, x: 0.545, y: 0.67 },
];

// ---- Electric and aviation-gas craft (#32) ---------------------------------
export const POWER = {
  ev: { label: 'EV', name: 'EV charging', unit: 'battery', icon: '⚡' },
  avgas: { label: 'AVGAS', name: 'AVGAS', unit: 'fuel', icon: '⛽' },
};
export const ENERGY_RESERVE = 0.1;    // a trip has to leave this much in hand
export const STRANDED_BELOW = 0.5;    // parked somewhere it can't refill, below this it's stranded
export const WRONG_SUPPLY_TURNAROUND = 3; // a hub drone at a hub without its supply sits this many times longer
export const POWER_FACT = 'Electric ag drones fly for minutes and charge between sorties; avgas helicopters fly for hours and refuel from a nurse truck. Every base has to match what lands there.';
